import { Spinner, Subtitle, BodyText } from '@kvdbil/components';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Head, { Props as HeadProps } from '~/App/shared/components/Head';
import Section from '~/App/shared/components/Layout/Section';
import { useParamFromQuery } from '~/App/shared/hooks/useQueryParams';
import ForgotPassword from '~/App/views/Member/views/ForgotPassword';
import { validateResetPasswordToken as validateResetPasswordTokenCall } from '~/helpers/orchestration/password';
import { useTranslation } from '~/Locale';
import CreatePasswordForm from '~/App/shared/components/ResetPasswordForm';
import { translateRoute } from '~/App/shared/localization';

const Container = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
`;

const Content = styled.div`
  padding: 2rem;
`;

const Description = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
`;

const StyledSpinner = styled(Spinner)`
  margin: 0 auto;
`;

const StyledSubtitle = styled(Subtitle)`
  font-weight: ${({ theme }) => theme.typography.fontAlternativeBoldWeight};
`;

interface Props {
  data?: HeadProps;
}

const CreatePassword = ({ data }: Props) => {
  const [isValidToken, setValidToken] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const token = useParamFromQuery('token') ?? '';
  const { t } = useTranslation();

  const validateResetPasswordToken = (createPasswordToken: string) => {
    validateResetPasswordTokenCall(createPasswordToken)
      .then(response => {
        setValidToken(response.data.isValidToken);
        setLoading(false);
      })
      .catch(() => {
        setValidToken(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    validateResetPasswordToken(token);
  }, [token]);

  if (isLoading) {
    return (
      <SpinnerContainer>
        <StyledSpinner color="neutral" speed="fast" />
      </SpinnerContainer>
    );
  }

  if (isValidToken) {
    return (
      <Container>
        <Head {...data} title={t('PAGE_TITLES.CREATE_PASSWORD')} />

        <Section
          contentBackgroundColor="light"
          maxWidth={32}
          backgroundColor="gray"
        >
          <Content>
            <Description>
              <StyledSubtitle as="h3">{t('Create password')}</StyledSubtitle>
              <BodyText>{t('Enter password.')}</BodyText>
            </Description>
            <CreatePasswordForm
              token={token}
              displayTermsCheckbox={true}
              redirectTo={translateRoute(
                t,
                '/ROUTES.MY_ACCOUNT/ROUTES.MY_ACCOUNT_SETTINGS'
              )}
            />
          </Content>
        </Section>
      </Container>
    );
  }

  return <ForgotPassword isBiddingView={false} isResetPassword={true} />;
};

export default CreatePassword;
